/**
 * Video Gallery Security Module
 * @module video-gallery-security
 *
 * Provides input validation and sanitization for gallery data and user interactions.
 * Exposes window-scoped functions for validation and error handling.
 *
 * Public APIs:
 * - validateVideoEntry(entry) — Full video object validation
 * - validateFilterValue(value, type) — Filter input sanitization
 * - handleThumbErrorSecure(img, platform, embedId) — Secure image error fallback
 * - setupThumbnailErrorHandlers(containerEl) — Attach error handlers to gallery grid
 *
 * Private Functions:
 * - validateEmbedId(embedId, platform) — Platform-specific embedId format check
 * - validateVideoUrl(url, platform) — URL domain + HTTPS validation
 * - getDefaultFallbackSvg() — SVG placeholder generator
 *
 * Schema:
 * - REQUIRED_FIELDS — all fields required in video entry
 * - VALID_PLATFORMS — 'youtube', 'tiktok', 'instagram'
 * - VALID_CATEGORIES — 7 content categories for filtering
 *
 * All functions follow defense-in-depth: validate type, validate format, validate domain.
 * No injection risk: all platform-specific URLs constructed from validated embedIds only.
 *
 * Usage:
 *   const result = validateVideoEntry({...});
 *   if (result.valid) renderVideo();
 */

(function () {
  'use strict';

  /* ========== Schema Validation ========== */

  /**
   * Required fields for a valid video entry
   * @type {Array<string>}
   * @private
   */
  const REQUIRED_FIELDS = [
    'id',
    'platform',
    'title',
    'description',
    'url',
    'embedId',
    'category',
    'tags',
    'creator',
    'date',
  ];

  /**
   * Valid platform values
   * @type {Object<string, boolean>}
   * @private
   */
  const VALID_PLATFORMS = {
    youtube: true,
    tiktok: true,
    instagram: true,
  };

  /**
   * Valid category values for video classification
   * @type {Object<string, boolean>}
   * @private
   */
  const VALID_CATEGORIES = {
    all: true,
    'box-reveal': true,
    unboxing: true,
    'journal-spread': true,
    'product-showcase': true,
    tutorial: true,
    'behind-the-scenes': true,
  };

  /**
   * Validate a video entry against schema
   *
   * Checks: all required fields present, valid platform, valid category,
   * tags is array, embedId format per platform, URL format per platform.
   *
   * @param {Object} entry - video entry to validate
   * @param {string} entry.id - unique video identifier
   * @param {string} entry.platform - 'youtube', 'tiktok', or 'instagram'
   * @param {string} entry.title - video title (max 80 chars recommended)
   * @param {string} entry.description - video description
   * @param {string} entry.url - full video URL (HTTPS required)
   * @param {string} entry.embedId - platform-specific embed ID (format validated per platform)
   * @param {string} entry.category - one of: all, box-reveal, unboxing, journal-spread, product-showcase, tutorial, behind-the-scenes
   * @param {Array<string>} entry.tags - array of tag strings (at least 1)
   * @param {string} entry.creator - creator name
   * @param {string} entry.date - ISO date string (YYYY-MM format)
   *
   * @returns {Object} validation result
   * @returns {boolean} result.valid - true if entry passes all validations
   * @returns {Array<string>} result.errors - array of error messages (empty if valid)
   *
   * @example
   * const result = validateVideoEntry({
   *   id: 'yt-001',
   *   platform: 'youtube',
   *   title: 'Box Reveal',
   *   description: 'Opening a mystery box',
   *   url: 'https://youtube.com/watch?v=dQw4w9WgXcQ',
   *   embedId: 'dQw4w9WgXcQ',
   *   category: 'box-reveal',
   *   tags: ['box', 'reveal', 'craft'],
   *   creator: 'Cora',
   *   date: '2026-04'
   * });
   * if (!result.valid) console.error(result.errors);
   */
  window.validateVideoEntry = function (entry) {
    const errors = [];

    if (!entry || typeof entry !== 'object') {
      errors.push('Entry must be an object');
      return { valid: false, errors };
    }

    // Check required fields
    for (let i = 0; i < REQUIRED_FIELDS.length; i++) {
      const field = REQUIRED_FIELDS[i];
      if (!(field in entry)) {
        errors.push('Missing required field: ' + field);
        if (window.VideoGalleryMetrics) {
          window.VideoGalleryMetrics.logValidationError(field, 'missing');
        }
      }
    }

    // Validate platform
    if (entry.platform && !VALID_PLATFORMS[entry.platform]) {
      errors.push(
        'Invalid platform: ' + entry.platform + '. Must be one of: youtube, tiktok, instagram'
      );
      if (window.VideoGalleryMetrics) {
        window.VideoGalleryMetrics.logValidationError(
          'platform',
          'invalid value: ' + entry.platform
        );
      }
    }

    // Validate category
    if (entry.category && !VALID_CATEGORIES[entry.category]) {
      errors.push('Invalid category: ' + entry.category);
      if (window.VideoGalleryMetrics) {
        window.VideoGalleryMetrics.logValidationError(
          'category',
          'invalid value: ' + entry.category
        );
      }
    }

    // Validate tags is array
    if (entry.tags && !Array.isArray(entry.tags)) {
      errors.push('Tags must be an array');
      if (window.VideoGalleryMetrics) {
        window.VideoGalleryMetrics.logValidationError('tags', 'not an array');
      }
    }

    // Validate embedId format (platform-specific)
    if (entry.embedId) {
      const embedError = validateEmbedId(entry.embedId, entry.platform);
      if (embedError) {
        errors.push(embedError);
        if (window.VideoGalleryMetrics) {
          window.VideoGalleryMetrics.logValidationError('embedId', embedError);
        }
      }
    }

    // Validate URL format
    if (entry.url) {
      const urlError = validateVideoUrl(entry.url, entry.platform);
      if (urlError) {
        errors.push(urlError);
        if (window.VideoGalleryMetrics) {
          window.VideoGalleryMetrics.logValidationError('url', urlError);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors,
    };
  };

  /**
   * Validate embedId format per platform
   *
   * Platform-specific format validation:
   * - YouTube: 11 alphanumeric chars (a-z, A-Z, 0-9, -, _)
   * - TikTok: 15-20 numeric digits
   * - Instagram: 10-15 alphanumeric chars (a-z, A-Z, 0-9, -, _)
   *
   * @param {string} embedId - embed identifier from platform
   * @param {string} platform - 'youtube', 'tiktok', or 'instagram'
   *
   * @returns {string|null} error message describing validation failure, or null if valid
   * @throws {Error} if platform is unrecognized (returns error string, not thrown)
   *
   * @example
   * validateEmbedId('dQw4w9WgXcQ', 'youtube') // null
   * validateEmbedId('abc123', 'youtube') // "YouTube embedId must be 11 alphanumeric..."
   */
  function validateEmbedId(embedId, platform) {
    if (typeof embedId !== 'string' || embedId.length === 0) {
      return 'embedId must be a non-empty string';
    }

    // YouTube: numeric IDs or alphanumeric with dashes/underscores (11 chars typical)
    if (platform === 'youtube') {
      if (!/^[A-Za-z0-9_-]{11}$/.test(embedId)) {
        return 'YouTube embedId must be 11 alphanumeric characters (with - or _)';
      }
    }

    // TikTok: numeric video IDs (15-20 digits typical)
    if (platform === 'tiktok') {
      if (!/^\d{15,20}$/.test(embedId)) {
        return 'TikTok embedId must be 15-20 numeric digits';
      }
    }

    // Instagram: alphanumeric shortcodes (10-12 chars typical)
    if (platform === 'instagram') {
      if (!/^[A-Za-z0-9_-]{10,15}$/.test(embedId)) {
        return 'Instagram embedId must be 10-15 alphanumeric characters';
      }
    }

    return null;
  }

  /**
   * Validate video URL matches platform
   *
   * Checks: HTTPS required, domain matches platform.
   * - YouTube: youtube.com or youtu.be
   * - TikTok: tiktok.com
   * - Instagram: instagram.com
   *
   * @param {string} url - full video URL to validate
   * @param {string} platform - 'youtube', 'tiktok', or 'instagram'
   *
   * @returns {string|null} error message describing validation failure, or null if valid
   *
   * @example
   * validateVideoUrl('https://youtube.com/watch?v=xyz', 'youtube') // null
   * validateVideoUrl('http://youtube.com/watch?v=xyz', 'youtube') // "URL must start with https://"
   */
  function validateVideoUrl(url, platform) {
    if (typeof url !== 'string' || url.length === 0) {
      return 'URL must be a non-empty string';
    }

    // Must be HTTPS
    if (!url.startsWith('https://')) {
      return 'URL must start with https://';
    }

    // Platform-specific domain checks
    if (platform === 'youtube') {
      if (!url.includes('youtube.com') && !url.includes('youtu.be')) {
        return 'YouTube URL must be from youtube.com or youtu.be domain';
      }
    }

    if (platform === 'tiktok') {
      if (!url.includes('tiktok.com')) {
        return 'TikTok URL must be from tiktok.com domain';
      }
    }

    if (platform === 'instagram') {
      if (!url.includes('instagram.com')) {
        return 'Instagram URL must be from instagram.com domain';
      }
    }

    return null;
  }

  /* ========== Filter Validation ========== */

  /**
   * Validate and sanitize filter values
   *
   * Applies validation per filter type and returns lowercase trimmed value
   * or null if invalid.
   *
   * Valid values per type:
   * - platform: 'youtube', 'tiktok', 'instagram'
   * - category: 'all', 'box-reveal', 'unboxing', 'journal-spread', 'product-showcase', 'tutorial', 'behind-the-scenes'
   * - sort: 'newest', 'oldest', 'title'
   *
   * @param {string} filterValue - filter value string to validate
   * @param {string} filterType - type of filter ('platform', 'category', or 'sort')
   *
   * @returns {string|null} sanitized lowercase value if valid, null if invalid
   *
   * @example
   * validateFilterValue('YouTube', 'platform') // 'youtube'
   * validateFilterValue('NEWEST', 'sort') // 'newest'
   * validateFilterValue('unknown', 'platform') // null
   */
  window.validateFilterValue = function (filterValue, filterType) {
    if (typeof filterValue !== 'string') {
      return null;
    }

    const trimmed = filterValue.trim().toLowerCase();

    if (filterType === 'platform') {
      return VALID_PLATFORMS[trimmed] ? trimmed : null;
    }

    if (filterType === 'category') {
      return VALID_CATEGORIES[trimmed] ? trimmed : null;
    }

    if (filterType === 'sort') {
      const validSorts = { newest: true, oldest: true, title: true };
      return validSorts[trimmed] ? trimmed : null;
    }

    return null;
  };

  /* ========== Thumbnail Error Handler with Security ========== */

  /**
   * Secure thumbnail error handler
   *
   * Called when image fails to load. Attempts one fallback for YouTube
   * (maxresdefault -> hqdefault), then uses default SVG placeholder.
   * Validates embedId before constructing any URL to prevent injection.
   *
   * Used in inline onerror attribute: onerror="handleThumbErrorSecure(this, 'youtube', 'abc123')"
   *
   * @param {HTMLImageElement} img - image element with failed src
   * @param {string} platform - 'youtube', 'tiktok', or 'instagram'
   * @param {string} embedId - video embedId (validated before use)
   *
   * @returns {void} updates img.src or img.onerror as side effect
   *
   * @example
   * // In card HTML:
   * <img src="..." onerror="handleThumbErrorSecure(this, 'youtube', 'dQw4w9WgXcQ')">
   */
  window.handleThumbErrorSecure = function (img, platform, embedId) {
    if (!img || typeof img.src !== 'string') {
      return;
    }

    // Validate embedId format
    const embedError = validateEmbedId(embedId, platform);
    if (embedError) {
      // Invalid embedId, skip fallback
      img.onerror = null;
      img.src = getDefaultFallbackSvg();
      return;
    }

    // YouTube: try hqdefault before giving up
    if (platform === 'youtube' && img.src.indexOf('maxresdefault') !== -1) {
      img.src = 'https://img.youtube.com/vi/' + embedId + '/hqdefault.jpg';
      return;
    }

    img.onerror = null;
    img.src = getDefaultFallbackSvg();
  };

  /**
   * Get default fallback SVG placeholder
   *
   * Returns a base64-encoded SVG showing a gradient background with a play button.
   * Used when video thumbnail fails to load and no platform-specific fallback is available.
   * Colors: gradient from #201030 to #101020 with play icon in #504070.
   *
   * @returns {string} data:image/svg+xml;base64 URL
   * @private
   */
  function getDefaultFallbackSvg() {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyNSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiMyMDEwMzAiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiMxMDEwMjAiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyNSIgZmlsbD0idXJsKCNnKSIvPjxjaXJjbGUgY3g9IjIwMCIgY3k9IjExMiIgcj0iMzAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzUwNDA3MCIgc3Ryb2tlLXdpZHRoPSIyIi8+PHBvbHlnb24gcG9pbnRzPSIxOTIsMTAwIDIxNSwxMTIgMTkyLDEyNCIgZmlsbD0iIzUwNDA3MCIvPjwvc3ZnPg==';
  }

  /**
   * Setup thumbnail error handlers via event delegation
   *
   * Attaches onerror listeners to all images with data-platform and data-embedid
   * attributes. Called after gallery renders to enable secure fallback handling.
   *
   * @param {HTMLElement} containerEl - container element holding image elements
   * @returns {void} attaches event listeners as side effect
   *
   * @example
   * const grid = document.getElementById('vg-grid');
   * setupThumbnailErrorHandlers(grid);
   */
  window.setupThumbnailErrorHandlers = function (containerEl) {
    if (!containerEl) {
      return;
    }

    // Use event delegation to catch image errors
    const images = containerEl.querySelectorAll('img[data-platform][data-embedid]');
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const platform = img.getAttribute('data-platform');
      const embedId = img.getAttribute('data-embedid');

      img.addEventListener('error', function () {
        window.handleThumbErrorSecure(this, platform, embedId);
      });
    }
  };

  window.safeSetHTML = function (el, html) {
    if (!el) return;
    el.replaceChildren(document.createRange().createContextualFragment(html));
  };

  /**
   * Handle on the module's private validators, for tests only.
   *
   * validateEmbedId and validateVideoUrl are reachable from the public API only
   * through validateVideoEntry, which skips both for a falsy value — so the
   * empty-string and non-string cases have no public route at all. Without this
   * handle a test suite has to transcribe them, and a transcription verifies the
   * copy rather than the shipped file: assets/video-gallery-security.js was not
   * required by its own test suite until 2026-08-23, and every assertion in it
   * ran against a hand-written duplicate that had already drifted (the real
   * fallback is a full base64 SVG, the copy was the literal string "fallback").
   * Additive and read-only; nothing in the module reads it back.
   *
   * Exported through CommonJS rather than a window global. Jest wraps a required
   * file, so `module` exists there and the suite gets its handle; the storefront
   * loads this as a plain <script> where `module` is undefined, so the block
   * never runs and no visitor's page carries a global that exists only to serve
   * tests. The typeof guard is what makes both true at once.
   */
  /* eslint-disable no-undef -- `module` is absent on the storefront, which is
     exactly what the typeof guard tests for. Matches the hook in
     assets/search-fallback.js and three sibling assets. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      validateEmbedId,
      validateVideoUrl,
      getDefaultFallbackSvg,
    };
  }
})();
